import { createContext, useContext } from 'react';

/**
 * THE IN-PROGRESS REPORT — M10 / S10-02.
 *
 * **THIS IS TEMPORARY STATE AND NOTHING ELSE.** It lives in the report
 * builder's route layout, in memory, for as long as Brandon is inside the
 * builder. There is **no `localStorage`, no `IndexedDB`, no draft row and no
 * report table** — saving a report is S10-05's job, and inventing storage here
 * would quietly change what "leaving loses the report" means.
 *
 * **WHY A ROUTE LAYOUT RATHER THAN A PROVIDER AT THE TOP OF THE APP.** The
 * builder now has two real addresses — choose Captures, then choose photos —
 * so Android's Back button walks between them. React Router renders a parent
 * route by wrapping it around its child, **with no identity key**, so the
 * parent instance survives while the child step changes. That gives exactly the
 * behaviour Jerry specified, enforced by structure rather than by discipline:
 *
 *   **BACK ONE STEP** — the layout never unmounted, so Captures, photos and the
 *   Description are all still here.
 *   **LEAVE THE BUILDER** — the layout unmounts and the in-progress report goes
 *   with it.
 *
 * Sol's visit store (M9) put its state above every route because a Sol answer
 * had to survive navigating to a Capture. A report must not survive leaving the
 * builder, so this deliberately sits lower.
 */

/** The photo's own stable identity — `client_photo_uuid` (`CapturePhotoView.key`). */
export type ReportPhotoKey = string;

export interface ReportBuilder {
  /** The Project this in-progress report belongs to. */
  readonly projectId: string;

  readonly captureIds: ReadonlySet<string>;
  readonly toggleCapture: (captureId: string) => void;

  /** Chosen photos for one Capture. **Empty is valid** — a Capture may contribute none. */
  readonly photoKeysFor: (captureId: string) => ReadonlySet<ReportPhotoKey>;
  readonly togglePhoto: (captureId: string, key: ReportPhotoKey) => void;

  /**
   * The photo keys that existed on a Capture immediately BEFORE Brandon tapped
   * Add, recorded so the newly added ones can be told apart afterwards.
   *
   * **`addPhotos` returns nothing**, and the library path can add several
   * photos at once, so a before-and-after comparison is the only way to know
   * which photos Brandon just created without changing the shared photo hook.
   *
   * It lives HERE rather than in the Capture section so it survives stepping
   * back and forward — which is what makes "a retry that succeeds later still
   * auto-selects" true rather than aspirational.
   */
  readonly baselineFor: (captureId: string) => ReadonlySet<ReportPhotoKey> | null;
  readonly beginAdd: (captureId: string, existingKeys: readonly ReportPhotoKey[]) => void;

  /** Already auto-selected once, so deselecting a new photo cannot be undone by a refresh. */
  readonly autoSelectedFor: (captureId: string) => ReadonlySet<ReportPhotoKey>;
  /**
   * Select photos that the SERVER has now accepted (D-3, and Jerry's M-2
   * ruling). Never called for a photo that is merely picked, processed or
   * staged — a failed upload must leave no phantom selection behind.
   */
  readonly autoSelectAttached: (captureId: string, keys: readonly ReportPhotoKey[]) => void;

  /** **OPTIONAL** (Jerry's decision 1). Blank is valid and blocks nothing. */
  readonly description: string;
  readonly setDescription: (next: string) => void;

  /**
   * Whether THIS report carries the company's identity — M10 / S10-03B.
   *
   * **THE ONLY BRANDING CONTROL IN THE REPORT BUILDER.** Company name, logo,
   * email and phone are set once in Settings and are not editable here; this is
   * a per-report include or exclude and nothing more.
   *
   * It lives in the builder rather than in Settings because it is a property of
   * one report, not of the business — and it lives in the LAYOUT so stepping
   * back to the photos and forward again does not silently re-brand a report
   * Brandon had chosen to send plain.
   */
  readonly includeBranding: boolean;
  readonly toggleBranding: () => void;
}

const ReportBuilderContext = createContext<ReportBuilder | null>(null);

export const ReportBuilderProvider = ReportBuilderContext.Provider;

/**
 * **THROWS RATHER THAN RETURNING A HOLLOW BUILDER.** A step rendered outside
 * the layout would otherwise collect selections into an object nobody owns and
 * lose them silently on the next tap — a failure that looks like a product bug
 * for as long as it takes somebody to find it.
 */
export function useReportBuilder(): ReportBuilder {
  const builder = useContext(ReportBuilderContext);
  if (!builder) throw new Error('A report step was rendered outside the report builder.');
  return builder;
}
